import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../ui/icon'
import { Button } from '../ui/button'
import { useDocumentManagerCapability, useOpenDocuments, useActiveDocument } from '@embedpdf/plugin-document-manager/react'
import { saveDocument, getAllDocuments, getDocument, type DocRecord, setPreference, getPreference } from '../../services/indexeddb-service'
import { OcrService } from '../../services/ocr/ocr-service'
import { setFileHandle } from '../../services/file-handle-service'
import { unsavedChangesRef } from './ContextualToolbar'

interface TabBarProps {
  activeDocumentId: string | null
}

export const TabBar = ({ activeDocumentId }: TabBarProps) => {
  const { t } = useTranslation()
  const { provides: docManager } = useDocumentManagerCapability()
  const { activeDocumentId: activeId } = useActiveDocument()
  const documents = useOpenDocuments()
  const inputRef = useRef<HTMLInputElement>(null)
  const [recentDocs, setRecentDocs] = useState<DocRecord[]>([])
  const [showRecent, setShowRecent] = useState(false)
  const [closeConfirm, setCloseConfirm] = useState<string | null>(null)

  useEffect(() => {
    getAllDocuments().then((docs) => setRecentDocs(docs.filter(d => d.id !== 'current-document')))
  }, [])

  const refreshRecent = () => getAllDocuments().then((docs) => setRecentDocs(docs.filter(d => d.id !== 'current-document')))

  const openListQueue: Array<() => Promise<void>> = []
  let processingQueue = false

  const processQueue = async () => {
    if (processingQueue) return
    processingQueue = true
    while (openListQueue.length > 0) {
      const next = openListQueue.shift()
      if (next) await next()
    }
    processingQueue = false
  }

  const addToOpenList = async (docId: string) => {
    openListQueue.push(async () => {
      const list = (await getPreference<string[]>('open-documents')) || []
      if (!list.includes(docId)) {
        await setPreference('open-documents', [...list, docId])
      }
    })
    processQueue()
  }

  const removeFromOpenList = async (docId: string) => {
    openListQueue.push(async () => {
      const list = (await getPreference<string[]>('open-documents')) || []
      await setPreference('open-documents', list.filter(id => id !== docId))
    })
    processQueue()
  }

  const openDocument = async (name: string, buffer: ArrayBuffer) => {
    if (!docManager) return
    const task = docManager.openDocumentBuffer({
      name,
      buffer,
      autoActivate: true,
    })
    task.wait(() => {
      OcrService.setDocumentName(name)
      const docRecord = {
        id: name,
        name,
        buffer,
        byteLength: buffer.byteLength,
        currentPage: 0,
        lastOpened: Date.now(),
      }
      saveDocument(docRecord).then(refreshRecent)
      saveDocument({ ...docRecord, id: 'current-document' }).catch(() => {})
      addToOpenList(name)
    }, () => {})
  }

  const handleOpenFilePicker = async () => {
    try {
      const [handle] = await (window as any).showOpenFilePicker({
        types: [{ description: 'PDF Files', accept: { 'application/pdf': ['.pdf'] } }],
        multiple: false,
      })
      const file = await handle.getFile()
      const buffer = await file.arrayBuffer()
      setFileHandle(handle)
      await openDocument(file.name, buffer)
    } catch {
      inputRef.current?.click()
    }
  }

  const handleOpenFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0]
    if (!file) return
    const buffer = await file.arrayBuffer()
    setFileHandle(null)
    await openDocument(file.name, buffer)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleSwitchTab = (docId: string) => {
    docManager?.setActiveDocument(docId)
    const name = docManager?.getDocumentState(docId)?.name || docId
    OcrService.setDocumentName(name)
  }

  const handleCloseTab = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    console.log('[TabBar] ========== handleCloseTab called for docId:', docId)
    if (unsavedChangesRef.current) {
      setCloseConfirm(docId)
    } else {
      console.log('[TabBar] Closing document without unsaved changes')
      const meta = docManager?.getDocumentState(docId)
      console.log('[TabBar] Document meta:', meta)
      if (meta?.name) {
        console.log('[TabBar] Removing from open-documents:', meta.name)
        await removeFromOpenList(meta.name)
      }
      docManager?.closeDocument(docId)
      console.log('[TabBar] Document closed from UI')
      // Check if this was the last document
      const allDocs = docManager?.getOpenDocuments?.()
      console.log('[TabBar] Remaining open documents after close:', allDocs?.length || 0)
      if (allDocs && allDocs.length === 0) {
        console.log('[TabBar] This was the last document - clearing current-document preference')
        // Could clear current-document here if needed
      }
    }
  }

  const handleForceClose = async () => {
    if (!closeConfirm) return
    console.log('[TabBar] ========== handleForceClose called for docId:', closeConfirm)
    unsavedChangesRef.current = false
    const meta = docManager?.getDocumentState(closeConfirm)
    console.log('[TabBar] Force close document meta:', meta)
    if (meta?.name) {
      console.log('[TabBar] Removing from open-documents:', meta.name)
      await removeFromOpenList(meta.name)
    }
    docManager?.closeDocument(closeConfirm)
    console.log('[TabBar] Force closed document from UI')
    // Check if this was the last document
    const allDocs = docManager?.getOpenDocuments?.()
    console.log('[TabBar] Remaining open documents after close:', allDocs?.length || 0)
    if (allDocs && allDocs.length === 0) {
      console.log('[TabBar] This was the last document - clearing current-document preference')
      // Could clear current-document here if needed
    }
    setCloseConfirm(null)
  }

  const handleOpenRecent = (doc: DocRecord) => {
    setFileHandle(null)
    getDocument(doc.id).then((latest) => {
      const buffer = latest?.buffer ?? doc.buffer
      openDocument(doc.name, buffer.slice(0))
    })
    saveDocument({ ...doc, lastOpened: Date.now() }).then(refreshRecent)
    setShowRecent(false)
  }

  const handleRemoveRecent = (name: string, e: React.MouseEvent) => {
    e.stopPropagation()
    import('../../services/indexeddb-service').then(({ deleteDocument }) => {
      deleteDocument(name).then(refreshRecent)
    })
  }

  const handleNewTab = () => {
    if (recentDocs.length > 0 && !showRecent) {
      setShowRecent(true)
    } else {
      setShowRecent(false)
      handleOpenFilePicker()
    }
  }

  return (
    <div className="h-10 bg-bg-elevated flex items-center px-2 gap-0.5 border-b-[0.5px] border-border-default/50 relative z-30">
      {/* Open document tabs */}
      {[...documents].reverse().map((doc) => {
        const isActive = doc.id === (activeId ?? activeDocumentId)
        const name = (doc.name || '').replace(/\.[^.]+$/, '').slice(0, 20) || t('tabBar.untitled')
        return (
          <div
            key={doc.id}
            onClick={() => handleSwitchTab(doc.id)}
            className={`flex items-center justify-between gap-1 px-3 h-8 rounded-t-lg border-x border-t border-border-default min-w-[100px] max-w-[180px] cursor-pointer transition-colors relative ${
              isActive
                ? 'bg-bg-surface text-fg-primary z-10'
                : 'bg-bg-surface-alt/50 text-fg-secondary hover:bg-bg-surface-alt border-border-default/50'
            }`}
          >
            {isActive && (
              <div className="absolute bottom-0 left-1 right-1 h-0.5 bg-accent rounded-full" />
            )}
            <span className="text-xs truncate select-none">{name}</span>
            <button
              onClick={(e) => handleCloseTab(doc.id, e)}
              className="h-4 w-4 p-0 flex items-center justify-center text-fg-muted hover:text-fg-primary flex-shrink-0 rounded-sm hover:bg-interactive-hover"
            >
              <Icon icon="x" size={10} />
            </button>
          </div>
        )
      })}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        id="tab-bar-file-input"
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleOpenFile}
      />

      {/* Add new tab button */}
      <div className="relative ml-1">
        <Button
          onClick={handleNewTab}
          className="h-7 w-7 p-0 text-fg-muted hover:text-fg-primary border-none hover:bg-interactive-hover"
        >
          <Icon icon="plus" size={14} />
        </Button>

        {/* Recent docs dropdown */}
        {showRecent && recentDocs.length > 0 && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-bg-surface border border-border-default rounded-lg shadow-xl z-50 py-1">
            <div className="px-3 py-1.5 text-xs text-fg-muted font-medium uppercase tracking-wider">
              {t('tabBar.recentDocuments')}
            </div>
            <button
              onClick={() => {
                setShowRecent(false)
                handleOpenFilePicker()
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
            >
              <Icon icon="plus" size={12} />
              {t('tabBar.openFile')}
            </button>
            {recentDocs.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between px-3 py-1.5 text-xs text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-alt transition-colors"
              >
                <span
                  className="truncate flex-1 cursor-pointer"
                  onClick={() => handleOpenRecent(doc)}
                  title={doc.name}
                >
                  {doc.name}
                </span>
                <button
                  onClick={(e) => handleRemoveRecent(doc.id, e)}
                  className="h-4 w-4 p-0 text-fg-muted hover:text-fg-primary flex-shrink-0 ml-1"
                >
                  <Icon icon="x" size={10} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {closeConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-bg-surface rounded-2xl shadow-2xl border border-border-default p-6 max-w-sm w-full mx-4">
            <h3 className="text-lg font-semibold text-fg-primary mb-2">{t('app.unsavedChangesTitle') || 'Unsaved Changes'}</h3>
            <p className="text-sm text-fg-muted mb-6">{t('app.unsavedChangesHint') || 'You have unsaved changes.'} {t('app.saveBeforeClosing') || 'Save from Menu → Save before closing, or close anyway.'}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleForceClose}
                className="w-full px-4 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600 transition-colors"
              >
                {t('app.closeAnyway') || 'Close Anyway'}
              </button>
              <button
                onClick={() => setCloseConfirm(null)}
                className="w-full px-4 py-2.5 text-fg-muted rounded-xl text-sm hover:text-fg-primary hover:bg-interactive-hover transition-colors"
              >
                {t('app.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
